import { AnalyticsEventTopic } from '~/modules/analytics/analyticsHelper';
import { FeatureFlags } from '~/modules/feature_flags';
import { SearchTotals } from '~/modules/stores/searchStore';
import { GA4SearchResultClickEvent, GTMBaseEvent, GtmEventType } from './gtmEventType';
import { determinePageType } from './gtmEventTypeHelper';

const ELEMENT_TYPE = 'aiOverviewListingCard';

// Slide 26 (data-layer AI Search Result Clicks): the search that produced this click is the AI
// Summary itself, so search_type is the fixed literal 'AI Search Summary' (matches slide's GA4
// custEv_searchResultClick configuration). Kept as a constant so a rename lands in one place.
const AI_SEARCH_SUMMARY_TYPE = 'AI Search Summary';

type AiOverviewCardClickEvent = {
    keyword?: string;
    listingId?: string;
    listingName?: string;
    listingUrl?: string;
    listingType?: string;
    position?: number;
    totals?: SearchTotals;
    aiSummaryResults?: number;
} & GTMBaseEvent;

// The slide's payload lists app_type/app_cost/featured/recommended too, but those are only known
// for real listing tiles (via pricing + record type). For an AI-summarised card in the overview,
// the metadata is title/publisher only — no pricing or record type reaches this handler, so we
// omit those fields rather than inventing values that would then dirty the GA4 dashboards.
export class GtmAiOverviewCardClickType extends GtmEventType {
    canHandle(event: GTMBaseEvent): boolean {
        return (
            event.topic === AnalyticsEventTopic.ANALYTICS_CLICK &&
            event.elementType === ELEMENT_TYPE &&
            FeatureFlags.FF_ACE_ENABLE_AI_LISTINGS_SUMMARY_ANALYTICS_EVENTS
        );
    }

    override getBody(event: GTMBaseEvent): GA4SearchResultClickEvent {
        const e = event as AiOverviewCardClickEvent;
        const apps = e.totals?.apps ?? 0;
        const consultants = e.totals?.consultants ?? 0;
        const agents = e.totals?.agents ?? 0;
        const slack = e.totals?.slack ?? 0;
        return {
            event: 'custEv_searchResultClick',
            element_type: 'tile',
            element_title: e.listingName ?? '',
            click_text: e.listingName ?? '',
            click_url: e.listingUrl ?? '',
            listing_id: e.listingId ?? '',
            listing_name: e.listingName ?? '',
            listing_type: e.listingType ?? '',
            listing_url: e.listingUrl ?? '',
            lp_detail_interaction: false,
            featured: false,
            recommended: false,
            ignore_hover: true,
            internal_article: false,
            hover_intent: false,
            internal_source: '',
            page_type: determinePageType(window.location.href),
            search_term: e.keyword ?? '',
            search_type: AI_SEARCH_SUMMARY_TYPE,
            search_category: 'all',
            search_result_clicks: 1,
            position: e.position,
            app_results: apps,
            consultant_results: consultants,
            agent_results: agents,
            // Salesforce platform = Apps, matching platformFilter.ts. Agents are a
            // `tech=force/copilot` subset of apps; Slack is reported on `slack_results`.
            salesforce_results: apps,
            slack_results: slack,
            ai_summary_results: e.aiSummaryResults ?? 0,
        };
    }
}
